import React from 'react';
import withStyles from 'isomorphic-style-loader/withStyles';

import Table from '../ui/Table';
import { Tabs, Tab } from '../ui/Tabs';
import { Grid } from '../ui/Grid';
import { AWS_URL } from '../../settings';
import s from './DataPage.css';

// Each era is a snapshot of HumanBase's data origins for a defined
// window. Add a new era when source data is reloaded in a way that
// would shift Q values for previously-saved URLs (annotation
// refresh, new ontology release, new compendium added, etc.).
// Append new entries to this array; the latest is the "current"
// era and is rendered as the leftmost (default-selected) tab.
//
// Source versions are hardcoded based on the actual files in
// humanbase-data/data/annotations/ and related directories.
// Each entry's `version` field is the data-version string from
// the file's header (for OBO files), the file's "Generated:"
// date (for OMIM, GAF), or the version embedded in the filename
// (for MSigDB, SFARI, HPRD). When a source file is bumped to a
// new version, copy the era forward, update the version strings
// in the new era, and append. Do not edit historical eras.
const eras = [
  {
    id: 'up-to-may2026',
    label: 'Up to May 2026',
    networks: [
      {
        name: 'GIANT',
        detail:
          '144 tissue/cell-lineage-specific functional networks plus ' +
          'one global functional network (Greene et al. 2015), ' +
          'built from 987 studies',
      },
    ],
    // Versions verified against prod's Database table on 2026-05-06.
    // For sources where prod stores an empty version string but the
    // source file carries its own metadata (uberon, GAFs), the file
    // metadata is shown.
    //
    // Each row carries an inline `// backing file:` comment so a
    // maintainer can match a public row to its on-disk source file
    // without exposing internal paths on the /data page.
    genes: [
      // backing file: data/genes/Homo_sapiens.gene_info
      // (NCBI gene_info has no embedded file-level version stamp;
      // the version shown is the file mtime on S3 at sync time.)
      {
        name: 'Homo sapiens gene records (NCBI Gene)',
        version: '2022-05-30',
        usedIn:
          'Foundational human gene records (Entrez IDs, symbols, ' +
          'descriptions, cross-references, aliases) used across all ' +
          'HumanBase tools, including the gene metadata shown for ' +
          'entries in the GIANT human compendium',
      },
    ],
    ontologies: [
      // backing file: go.obo
      {
        name: 'Gene Ontology (Biological Process)',
        version: 'releases/2018-05-14',
        usedIn:
          "FMD enrichment, Networks: 'Process' panel and gene predictions, SEEK (beta)",
      },
      // backing file: doid-non-classified.obo
      {
        name: 'Disease Ontology',
        version: 'releases/2017-06-13',
        usedIn:
          "FMD enrichment, Networks: 'Process' panel and gene predictions",
      },
      // backing file: uberon.obo
      {
        name: 'Uberon',
        version: 'uberon/releases/2022-06-30/ext.owl',
        usedIn:
          'Dataset tissue tags in SEEK (beta) metadata ' +
          '(not a term-enrichment source)',
      },
      // backing file: BrendaTissueOBO (live URL, fetched at load time)
      {
        name: 'BRENDA Ontology',
        version: 'releases/2021-10-26',
        usedIn: "Networks: 'Tissue' panel",
      },
    ],
    annotations: [
      // backing file: goa_human.gaf
      {
        name: 'GO BP annotations: Homo sapiens (UniProt-GOA)',
        version: '2018-03-26',
        usedIn:
          "FMD enrichment, Networks: 'Process' panel and gene predictions, SEEK (beta)",
      },
      // backing files: mgi.gaf, wb.gaf, fb.gaf, sgd.gaf
      {
        name:
          'GO BP annotations: Mus musculus, Caenorhabditis ' +
          'elegans, Drosophila melanogaster, Saccharomyces ' +
          'cerevisiae (GAF)',
        version: '2022-05-17',
        usedIn: 'SEEK (beta)',
      },
      // backing files: h.all.v2023.2.Hs.json + h.all.v2023.2.Hs.gmt
      {
        name: 'MSigDB Hallmark (H)',
        version: 'MSigDB_2023.2.Hs',
        usedIn: 'FMD enrichment',
      },
      // backing files: c2.cp.v2023.2.Hs.json + c2.cp.v2023.2.Hs.gmt
      {
        name: 'MSigDB Canonical Pathways (C2-CP)',
        version: 'MSigDB_2023.2.Hs',
        usedIn: 'FMD enrichment',
      },
      // backing file: SFARI-Gene_human-gene-scores_release_04-17-2018.csv
      {
        name: 'SFARI gene scores',
        version: 'SFARI-Gene_human-gene-scores_release_04-17-2018',
        usedIn: "Networks: 'Process' panel",
      },
      // backing files: mim2gene.txt, genemap.txt
      {
        name: 'OMIM',
        version: '2018-05-17',
        usedIn:
          'Folded into Disease Ontology terms (not a ' +
          'separate annotation source)',
      },
      // backing file: HPRD/FLAT_FILES_072010/TISSUE_EXPRESSIONS.txt
      {
        name: 'HPRD tissue annotations',
        version: 'Release 9',
        usedIn:
          'Source for BRENDA tissue mapping at load time ' +
          '(not directly visible as terms)',
      },
    ],
    compendia: [
      {
        title: 'Human Compendium (GIANT)',
        count: 990,
        file: 'human_compendium.tsv',
      },
    ],
    notableChange: null,
    reproducibilityNote: (
      <p>
        To reproduce a GIANT FMD or Networks term-enrichment result generated in
        this era, see{' '}
        <a
          href="https://humanbase.readthedocs.io/en/latest/reproducibility.html"
          target="_blank"
          rel="noopener noreferrer"
        >
          Reproducing legacy results
        </a>
        .
      </p>
    ),
  },
  {
    id: 'jun2026-present',
    label: 'Jun 2026 – present',
    networks: [
      {
        name: 'MAGE',
        detail:
          '289 tissue/cell-type-specific functional networks plus ' +
          'one global functional network, built from 7,463 studies ' +
          'using a two-stage masked graph autoencoder and ' +
          'tissue-specific gradient-boosting integration framework',
      },
      {
        name: 'GIANT',
        detail:
          '144 tissue/cell-lineage-specific functional networks plus ' +
          'one global functional network (Greene et al. 2015), ' +
          'built from 987 studies',
      },
    ],
    // Identical to Era 1 except for the NCBI gene2go addition and
    // the MAGE-curated human gene set. If you reload any of the
    // other source files with a newer version, copy this era
    // forward to a new entry and update the version strings in the
    // new era. Do not mutate this one.
    //
    // Each row carries an inline `// backing file:` comment so a
    // maintainer can match a public row to its on-disk source file
    // without exposing internal paths on the /data page.
    genes: [
      // backing file: data/genes/Homo_sapiens.gene_info
      // (same NCBI sync as Era 1 — no refresh at the era boundary.)
      {
        name: 'Homo sapiens gene records (NCBI Gene)',
        version: '2022-05-30',
        usedIn:
          'Foundational human gene records (Entrez IDs, symbols, ' +
          'descriptions, cross-references, aliases) used across all ' +
          'HumanBase tools, including the gene metadata shown for ' +
          'entries in the GIANT human compendium',
      },
      // backing file:
      // data/compendium/mage/MAGE_gene_information.tsv
      // (MAGE-curated gene set; no embedded version stamp; the
      // version shown is the file mtime captured at load time.)
      {
        name: 'MAGE-curated gene records (NCBI Gene)',
        version: '2026-01-08',
        usedIn:
          'Gene index for the MAGE human compendium ' +
          '(36,077 genes including ncRNA, pseudo, snoRNA, and ' +
          'other types not present in GIANT)',
      },
    ],
    ontologies: [
      // backing file: go.obo
      {
        name: 'Gene Ontology (Biological Process)',
        version: 'releases/2018-05-14',
        usedIn:
          "FMD enrichment (GIANT + MAGE), Networks: 'Process' panel " +
          'and gene predictions, SEEK (beta)',
      },
      // backing file: doid-non-classified.obo
      {
        name: 'Disease Ontology',
        version: 'releases/2017-06-13',
        usedIn:
          "FMD enrichment (GIANT + MAGE), Networks: 'Process' panel " +
          'and gene predictions',
      },
      // backing file: uberon.obo
      {
        name: 'Uberon',
        version: 'uberon/releases/2022-06-30/ext.owl',
        usedIn:
          'Dataset tissue tags in SEEK (beta) metadata ' +
          '(not a term-enrichment source)',
      },
      // backing file: BrendaTissueOBO (live URL, fetched at load time)
      {
        name: 'BRENDA Ontology',
        version: 'releases/2021-10-26',
        usedIn: "Networks: 'Tissue' panel",
      },
    ],
    annotations: [
      // backing file: gene2go_human_bp.tsv
      // (gene2go has no embedded version stamp; the version
      // shown is the file mtime captured at load time.)
      {
        name: 'GO BP annotations: Homo sapiens (NCBI gene2go)',
        version: '2026-03-16',
        usedIn: 'FMD enrichment (MAGE)',
      },
      // backing file: goa_human.gaf
      {
        name: 'GO BP annotations: Homo sapiens (UniProt-GOA)',
        version: '2018-03-26',
        usedIn:
          "FMD enrichment (GIANT), Networks: 'Process' panel " +
          'and gene predictions, SEEK (beta)',
      },
      // backing files: mgi.gaf, wb.gaf, fb.gaf, sgd.gaf
      {
        name:
          'GO BP annotations: Mus musculus, Caenorhabditis ' +
          'elegans, Drosophila melanogaster, Saccharomyces ' +
          'cerevisiae (GAF)',
        version: '2022-05-17',
        usedIn: 'SEEK (beta)',
      },
      // backing files: h.all.v2023.2.Hs.json + h.all.v2023.2.Hs.gmt
      {
        name: 'MSigDB Hallmark (H)',
        version: 'MSigDB_2023.2.Hs',
        usedIn: 'FMD enrichment (GIANT + MAGE)',
      },
      // backing files: c2.cp.v2023.2.Hs.json + c2.cp.v2023.2.Hs.gmt
      {
        name: 'MSigDB Canonical Pathways (C2-CP)',
        version: 'MSigDB_2023.2.Hs',
        usedIn: 'FMD enrichment (GIANT + MAGE)',
      },
      // backing file: SFARI-Gene_human-gene-scores_release_04-17-2018.csv
      {
        name: 'SFARI gene scores',
        version: 'SFARI-Gene_human-gene-scores_release_04-17-2018',
        usedIn: "Networks: 'Process' panel",
      },
      // backing files: mim2gene.txt, genemap.txt
      {
        name: 'OMIM',
        version: '2018-05-17',
        usedIn:
          'Folded into Disease Ontology terms (not a ' +
          'separate annotation source)',
      },
      // backing file: HPRD/FLAT_FILES_072010/TISSUE_EXPRESSIONS.txt
      {
        name: 'HPRD tissue annotations',
        version: 'Release 9',
        usedIn:
          'Source for BRENDA tissue mapping at load time ' +
          '(not directly visible as terms)',
      },
    ],
    compendia: [
      {
        // backing file:
        // humanbase-data/data/compendium/mage/human_data_compendium.tsv
        // (7,464 lines = 1 header + 7,463 dataset rows; columns:
        // Name, Title, Description, Data type). Link assumes the
        // file is published to ${AWS_URL}/compendia/ alongside the
        // GIANT compendium. If it isn't there yet, set file: null
        // to render "not yet published" instead of a broken link.
        title: 'Human Compendium (MAGE)',
        count: 7463,
        file: 'mage_human_compendium.tsv',
      },
      {
        title: 'Human Compendium (GIANT)',
        count: 990,
        file: 'human_compendium.tsv',
      },
    ],
    notableChange: null,
  },
];

// modSeek compendia are organism-scoped reference files used by
// the SEEK gene-coexpression search engine. They are not affected
// by the term-enrichment changes that drive era boundaries, so
// they live outside the era tabs.
const seekCompendia = [
  {
    id: 2,
    title: 'modSeek homo-sapiens',
    count: 12842,
    file: 'modseek_human.tsv',
  },
  {
    id: 5,
    title: 'modSeek mus-musculus',
    count: 12056,
    file: 'modseek_mouse.tsv',
  },
  {
    id: 4,
    title: 'modSeek caenorhabditis-elegans',
    count: 398,
    file: 'modseek_worm.tsv',
  },
  {
    id: 6,
    title: 'modSeek drosophila-melanogaster',
    count: 786,
    file: 'modseek_fly.tsv',
  },
  {
    id: 3,
    title: 'modSeek danio-rerio',
    count: 343,
    file: 'modseek_zebrafish.tsv',
  },
  {
    id: 7,
    title: 'modSeek saccharomyces-cerevisiae',
    count: 400,
    file: 'modseek_yeast.tsv',
  },
];

const compendiumRow = compendium => (
  <tr key={compendium.id || compendium.title}>
    <td style={{ width: '33%', verticalAlign: 'middle' }}>
      {compendium.title}
    </td>
    <td style={{ width: '33%', verticalAlign: 'middle' }}>
      {compendium.count.toLocaleString()}
    </td>
    <td style={{ width: '34%', verticalAlign: 'middle' }}>
      {compendium.file ? (
        <a href={`${AWS_URL}/compendia/${compendium.file}`} download>
          {compendium.file}
        </a>
      ) : (
        <em>not yet published</em>
      )}
    </td>
  </tr>
);

const networkRow = n => (
  <tr key={n.name}>
    <td style={{ width: 175, verticalAlign: 'middle' }}>{n.name}</td>
    <td style={{ verticalAlign: 'middle' }}>{n.detail}</td>
  </tr>
);

const sourceRow = src => (
  <tr key={src.name}>
    <td style={{ width: '33%', verticalAlign: 'middle' }}>{src.name}</td>
    <td style={{ width: '33%', verticalAlign: 'middle' }}>{src.version}</td>
    <td style={{ width: '34%', verticalAlign: 'middle' }}>{src.usedIn}</td>
  </tr>
);

const subheaderNoteStyle = {
  fontSize: 18,
  color: '#888',
  marginTop: -5,
  marginBottom: 12,
};

const renderEra = era => (
  <div key={era.id}>
    {era.notableChange ? (
      <p>
        <em>
          <strong>Notable change in this era:</strong> {era.notableChange}
        </em>
      </p>
    ) : null}

    <h3>Networks</h3>
    <p style={subheaderNoteStyle}>
      Tissue and cell-lineage interaction networks built from integrated
      genomics data.
    </p>
    <Table striped>
      <thead>
        <tr>
          <th>Name</th>
          <th>Detail</th>
        </tr>
      </thead>
      <tbody>{era.networks.map(networkRow)}</tbody>
    </Table>

    <h3>Gene records</h3>
    <p style={subheaderNoteStyle}>
      Per-gene identifiers, symbols, types, descriptions, and cross-references
      used across HumanBase tools.
    </p>
    <Table striped>
      <thead>
        <tr>
          <th>Name</th>
          <th>Version</th>
          <th>Used in</th>
        </tr>
      </thead>
      <tbody>{era.genes.map(sourceRow)}</tbody>
    </Table>

    <h3>Ontologies</h3>
    <p style={subheaderNoteStyle}>
      Hierarchies of terms used in HumanBase enrichment analyses and
      predictions.
    </p>
    <Table striped>
      <thead>
        <tr>
          <th>Name</th>
          <th>Version</th>
          <th>Used in</th>
        </tr>
      </thead>
      <tbody>{era.ontologies.map(sourceRow)}</tbody>
    </Table>

    <h3>Gene annotations</h3>
    <p style={subheaderNoteStyle}>
      Gene-to-term mappings used in enrichment analyses, term predictions, and
      curated gene scores.
    </p>
    <Table striped>
      <thead>
        <tr>
          <th>Name</th>
          <th>Version</th>
          <th>Used in</th>
        </tr>
      </thead>
      <tbody>{era.annotations.map(sourceRow)}</tbody>
    </Table>

    <h3>Network compendia</h3>
    <p style={subheaderNoteStyle}>
      Genomics studies used to build the networks.
    </p>
    <Table striped>
      <thead>
        <tr>
          <th>Name</th>
          <th>Studies</th>
          <th>Download</th>
        </tr>
      </thead>
      <tbody>{era.compendia.map(compendiumRow)}</tbody>
    </Table>

    {era.reproducibilityNote ? (
      <>
        <h3>About reproducibility for this era</h3>
        {era.reproducibilityNote}
      </>
    ) : null}
  </div>
);

const DataPage = () => {
  // Render most-recent-first so the current era is the leftmost
  // (and default-selected) tab; the underlying eras array stays in
  // chronological order so appending a new era is a tail-append.
  const erasForDisplay = eras.slice().reverse();

  return (
    <div className={s.root}>
      <Grid>
        <h1>HumanBase Data Sources</h1>
        <p style={{ marginBottom: 30 }}>
          HumanBase&apos;s source data is refreshed periodically from NCBI, Gene
          Ontology, MSigDB and other external repositories. The tabs below
          describe the data in effect during each era.
        </p>

        <Tabs id="data-eras" defaultActiveKey={1} bsStyle="tabs">
          {erasForDisplay.map((era, i) => (
            <Tab key={era.id} eventKey={i + 1} title={era.label}>
              {renderEra(era)}
            </Tab>
          ))}
        </Tabs>

        <div style={{ padding: '15px' }}>
          <h2 style={{ marginTop: 30 }}>SEEK (beta) data compendia</h2>
          <p style={subheaderNoteStyle}>
            Genomics studies used by the SEEK (beta) gene-coexpression search
            engine.
          </p>
          <Table striped>
            <thead>
              <tr>
                <th>Name</th>
                <th>Studies</th>
                <th>Download</th>
              </tr>
            </thead>
            <tbody>{seekCompendia.map(compendiumRow)}</tbody>
          </Table>

          <h2 style={{ marginTop: 30 }}>Variant effect prediction models</h2>
          <p style={subheaderNoteStyle}>
            Training data sources for deep learning frameworks.
          </p>
          <Table striped>
            <thead>
              <tr>
                <th>Model</th>
                <th>Training Data Sources</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ verticalAlign: 'top', width: '150px' }}>
                  <strong>DeepSEA (Beluga)</strong>
                </td>
                <td>
                  Trained on 2,002 chromatin profile features, including DNase
                  hypersensitivity and ChIP-seq profiles. The complete list of
                  features used to train Beluga can be found at{' '}
                  <a
                    href="https://s3-us-west-2.amazonaws.com/humanbase-dev/deepsea/examples/41588_2019_420_MOESM9_ESM.csv"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    this CSV file
                  </a>
                  .
                </td>
              </tr>
              <tr>
                <td style={{ verticalAlign: 'top' }}>
                  <strong>Sei</strong>
                </td>
                <td>
                  Trained on 21,907 chromatin profiles, including DNase
                  hypersensitivity, ChIP-seq, and ATAC-seq profiles. The list of
                  profiles incorporated in the Sei model can be found in
                  Supplementary Table 1 of{' '}
                  <a
                    href="https://www.nature.com/articles/s41588-022-01102-2"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Chen et al 2022
                  </a>
                  .
                </td>
              </tr>
              <tr>
                <td style={{ verticalAlign: 'top' }}>
                  <strong>ExPecto</strong>
                </td>
                <td>
                  Incorporated the Beluga chromatin profile model as well as 218
                  tissue expression profiles from GTEx, Roadmap Epigenomics and
                  ENCODE (
                  <a
                    href="https://www.nature.com/articles/s41588-018-0160-6"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Zhou et al. 2018
                  </a>
                  ).
                </td>
              </tr>
              <tr>
                <td style={{ verticalAlign: 'top' }}>
                  <strong>ExPectoSC</strong>
                </td>
                <td>
                  Incorporated the Beluga chromatin profile model as well as
                  single-cell gene expression profiles for over 100 cell types
                  from seven organ systems (listed in Supplementary Table 1 of{' '}
                  <a
                    href="https://www.cell.com/cell-reports-methods/fulltext/S2667-2375(23)00224-2"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Sokolova et al. 2023
                  </a>
                  ).
                </td>
              </tr>
              <tr>
                <td style={{ verticalAlign: 'top' }}>
                  <strong>Seqweaver</strong>
                </td>
                <td>
                  Trained using data from 232 cross-linking immunoprecipitation
                  (CLIP) based RNA binding protein datasets (Supplementary Table
                  1 of{' '}
                  <a
                    href="https://www.nature.com/articles/s41588-020-00761-3"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Park et al. 2021
                  </a>
                  ).
                </td>
              </tr>
            </tbody>
          </Table>
        </div>
      </Grid>
    </div>
  );
};

export default withStyles(s)(DataPage);
